import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, CATEGORY_LABELS } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, LogOut, Package, Mail } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", category: "pokemon", price: "", image: "", description: "", stock: 10, featured: false };
const CATS = ["pokemon", "sports", "graded", "sealed"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([api.get("/products"), api.get("/contacts")]);
      setProducts(p.data);
      setContacts(c.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("het_admin_token");
        navigate("/admin/login");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("het_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const logout = () => {
    localStorage.removeItem("het_admin_token");
    navigate("/admin/login");
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, price: String(p.price) }); setModalOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock, 10) || 0 };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      setModalOpen(false);
      load();
    } catch {
      toast.error("Could not save product");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const set = (k) => (e) => {
    const v = k === "featured" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="admin-dashboard">
      <header className="bg-[#262626] text-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-[#D32F2F] text-white font-display font-black px-2.5 py-1 rounded">HE</span>
            <h1 className="font-display font-bold text-lg">Admin Dashboard</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm font-semibold hover:text-[#D32F2F] transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex gap-3 mb-8">
          <button onClick={() => setTab("products")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold ${tab === "products" ? "bg-[#D32F2F] text-white" : "bg-white border border-[#262626]/15"}`} data-testid="tab-products">
            <Package className="w-4 h-4" /> Products ({products.length})
          </button>
          <button onClick={() => setTab("contacts")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold ${tab === "contacts" ? "bg-[#D32F2F] text-white" : "bg-white border border-[#262626]/15"}`} data-testid="tab-contacts">
            <Mail className="w-4 h-4" /> Inquiries ({contacts.length})
          </button>
        </div>

        {tab === "products" ? (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display font-bold text-2xl text-[#262626]">Manage Products</h2>
              <button onClick={openNew} data-testid="add-product-button" className="inline-flex items-center gap-2 bg-[#D32F2F] text-white font-bold px-5 py-2.5 rounded-md hover:bg-[#B71C1C] transition-colors">
                <Plus className="w-4 h-4" /> Add Product
              </button>
            </div>
            <div className="bg-white rounded-lg border border-[#262626]/10 overflow-hidden">
              <div className="divide-y divide-[#262626]/10">
                {products.map((p) => (
                  <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`admin-product-${p.id}`}>
                    {p.image ? (
                      <img src={p.image} alt="" className="w-14 h-14 rounded-md object-cover bg-[#F9F6F0]" />
                    ) : (
                      <div className="w-14 h-14 rounded-md bg-[#F9F6F0] flex items-center justify-center"><Package className="w-5 h-5 text-[#262626]/30" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#262626] truncate">{p.name}</p>
                      <p className="text-sm text-[#262626]/60">{CATEGORY_LABELS[p.category]} · ${p.price.toFixed(2)} · {p.stock} in stock {p.featured && "· ⭐ Featured"}</p>
                    </div>
                    <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="p-2 rounded hover:bg-[#262626]/5"><Pencil className="w-4 h-4 text-[#262626]" /></button>
                    <button onClick={() => remove(p)} data-testid={`delete-product-${p.id}`} className="p-2 rounded hover:bg-[#D32F2F]/10"><Trash2 className="w-4 h-4 text-[#D32F2F]" /></button>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white rounded-lg border border-[#262626]/10 divide-y divide-[#262626]/10">
            {contacts.length === 0 ? (
              <p className="p-8 text-center text-[#262626]/60">No inquiries yet.</p>
            ) : contacts.map((c) => (
              <div key={c.id} className="p-5" data-testid={`contact-row-${c.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-[#262626]">{c.name} <span className="text-[#262626]/50 font-normal">· {c.email}</span></p>
                    {c.subject && <p className="text-sm font-semibold text-[#D32F2F] mt-1">{c.subject}</p>}
                  </div>
                  <span className="text-xs text-[#262626]/40">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-[#262626]/75 mt-2 text-sm">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#F9F6F0] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-modal">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl text-[#262626]">{editing ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-bold mb-1.5">Name</label>
              <input required value={form.name} onChange={set("name")} data-testid="form-name" className="w-full rounded-md border border-[#262626]/20 px-3 py-2.5 bg-white focus:ring-2 focus:ring-[#D32F2F] focus:outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold mb-1.5">Category</label>
                <select value={form.category} onChange={set("category")} data-testid="form-category" className="w-full rounded-md border border-[#262626]/20 px-3 py-2.5 bg-white focus:ring-2 focus:ring-[#D32F2F] focus:outline-none">
                  {CATS.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold mb-1.5">Price ($)</label>
                <input required type="number" step="0.01" min="0" value={form.price} onChange={set("price")} data-testid="form-price" className="w-full rounded-md border border-[#262626]/20 px-3 py-2.5 bg-white focus:ring-2 focus:ring-[#D32F2F] focus:outline-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold mb-1.5">Stock</label>
                <input type="number" min="0" value={form.stock} onChange={set("stock")} data-testid="form-stock" className="w-full rounded-md border border-[#262626]/20 px-3 py-2.5 bg-white focus:ring-2 focus:ring-[#D32F2F] focus:outline-none" />
              </div>
              <div className="flex items-center gap-2 pt-7">
                <input type="checkbox" id="featured" checked={form.featured} onChange={set("featured")} data-testid="form-featured" className="w-4 h-4 accent-[#D32F2F]" />
                <label htmlFor="featured" className="text-sm font-bold">Featured on homepage</label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold mb-1.5">Image URL</label>
              <input value={form.image} onChange={set("image")} data-testid="form-image" className="w-full rounded-md border border-[#262626]/20 px-3 py-2.5 bg-white focus:ring-2 focus:ring-[#D32F2F] focus:outline-none" placeholder="https://…" />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1.5">Description</label>
              <textarea rows={3} value={form.description} onChange={set("description")} data-testid="form-description" className="w-full rounded-md border border-[#262626]/20 px-3 py-2.5 bg-white focus:ring-2 focus:ring-[#D32F2F] focus:outline-none resize-none" />
            </div>
            <button type="submit" disabled={saving} data-testid="form-submit" className="w-full bg-[#D32F2F] text-white font-bold py-3 rounded-md hover:bg-[#B71C1C] transition-colors disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Update Product" : "Add Product"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
